// ============================================================
// BARREL FILE - Centralized API exports
// ============================================================
// Import and re-export all admin API functions from modules
export { loginAdmin, verifyAdminToken, changeAdminPassword, getAdminProfile } from "./modules/auth";
export { createAdmin, updateAdmin, resetAdminPassword, deleteAdmin, getAllAdmins, getAuditHistory } from "./modules/adminManagement";
export { getStatsSummary, getDailyStats, getMonthlyStats, getYearlyStats, getPageStats, exportStatsJson } from "./modules/stats";
export { getAnnouncements, createAnnouncement, updateAnnouncement, deleteAnnouncement } from "./modules/announcements";
export { getDonations, createDonation, updateDonation, deleteDonation } from "./modules/donations";
export { getHomepageCards, createHomepageCard, updateHomepageCard, deleteHomepageCard } from "./modules/homepages";
export {
  getLogostripSections,
  createLogostripSection,
  updateLogostripSection,
  deleteLogostripSection,
  createLogostripItem,
  updateLogostripItem,
  deleteLogostripItem,
} from "./modules/logostrip";
export { getPageContent, updatePageContent } from "./modules/pages";
export { uploadImage, uploadReportPdf, deleteUploadedImage } from "./modules/upload";
export { getApiBaseUrl, getAuthToken } from "./modules/baseApi";
export { getPublicationsAdmin, createPublication, updatePublication, deletePublication } from "./modules/publications";
export { getOpenSourceAdmin, createOpenSource, updateOpenSource, deleteOpenSource } from "./modules/openSource";
export { getResearchAdmin, createResearch, updateResearch, deleteResearch } from "./modules/research";
export { getWebinarsAdmin, createWebinar, updateWebinar, deleteWebinar } from "./modules/webinars";
export { getMembersAdmin, createMember, updateMember, deleteMember } from "./modules/members";
export { getPartnersAdmin, createPartner, updatePartner, deletePartner } from "./modules/partners";

// ============================================================
// PUBLIC API FUNCTIONS (for non-admin pages)
// ============================================================

const DEFAULT_API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const LOOPBACK_HOSTNAMES = new Set(["localhost", "127.0.0.1", "::1"]);

function normalizeBaseUrl(baseUrl) {
  if (!baseUrl) {
    return "";
  }
  return baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
}

function getPublicApiBaseUrl() {
  const baseUrl = normalizeBaseUrl(DEFAULT_API_BASE_URL);
  if (!baseUrl) {
    return "/api";
  }

  if (typeof window === "undefined") {
    return baseUrl;
  }

  try {
    const parsedBaseUrl = new URL(baseUrl, window.location.origin);
    const isLoopbackTarget = LOOPBACK_HOSTNAMES.has(parsedBaseUrl.hostname);
    const isLoopbackCurrentOrigin = LOOPBACK_HOSTNAMES.has(window.location.hostname);

    if (isLoopbackTarget && !isLoopbackCurrentOrigin) {
      return "/api";
    }
  } catch {
    return baseUrl;
  }

  return baseUrl;
}

async function fetchPublicAPI(endpoint, language) {
  const baseUrl = getPublicApiBaseUrl();

  const params = new URLSearchParams();
  if (language) {
    params.set("lang", language);
  }

  const url = `${baseUrl}${endpoint}${params.toString() ? `?${params.toString()}` : ""}`;

  try {
    const response = await fetch(url);
    if (!response.ok) {
      return null;
    }
    return await response.json();
  } catch (error) {
    console.error(`Error fetching ${endpoint}:`, error);
    return null;
  }
}

export async function fetchMembersContent(language) {
  return fetchPublicAPI("/members", language);
}

export async function fetchOpenSourceContent(language) {
  return fetchPublicAPI("/open-source", language);
}

export async function fetchPartnersContent(language) {
  return fetchPublicAPI("/partners", language);
}

export async function fetchPublicationsContent(language) {
  return fetchPublicAPI("/publications", language);
}

export async function fetchResearchContent(language) {
  return fetchPublicAPI("/research", language);
}

export async function fetchWebinarsContent(language) {
  return fetchPublicAPI("/webinars", language);
}

export async function fetchHomepageContent(language) {
  return fetchPublicAPI("/homepage", language);
}

export async function fetchAnnouncementContent(language) {
  return fetchPublicAPI("/announcement", language);
}

export async function fetchDonationsContent(language) {
  return fetchPublicAPI("/donations", language);
}
